import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, saveGroupTemplate } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GroupTemplate } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface RenameGroupTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedTemplate: GroupTemplate;
  changeLogRoles: string[];
}

export default function RenameGroupTemplateDialog({
  open,
  handleClose,
  selectedTemplate,
  changeLogRoles,
}: RenameGroupTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  interface FormInput {
    templateName: string;
    templateDescription: string;
  }

  const values = {
    templateName: selectedTemplate.groupTemplateName,
    templateDescription: selectedTemplate.groupTemplateDesc,
  };

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<FormInput>({
    values: values,
  });
  const onSubmit = (data: FormInput) => {
    const updatedTemplate: GroupTemplate = {
      ...selectedTemplate,
      groupTemplateName: data.templateName,
      groupTemplateDesc: data.templateDescription,
    };
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupTemplates.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(updatedTemplate);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: GroupTemplate) =>
      saveGroupTemplate(data, changeLogMessage),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group Template updated successfully!',
      });

      queryClient.setQueryData<GroupTemplate[]>(
        [UserKeys.GroupTemplates],
        (old) => {
          if (old) {
            return old.map((template) =>
              template.keyString === data.keyString
                ? {
                    ...template,
                    groupTemplateName: data.groupTemplateName,
                    groupTemplateDesc: data.groupTemplateDesc,
                  }
                : template
            );
          }
          return [];
        }
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this group template. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_templates.rename_group_template, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groupTemplates,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Rename Group Template"
      dialogContent={
        <Box>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Controller
              name={'templateName'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'templateName',
                  }}
                  InputLabelProps={{
                    htmlFor: 'templateName',
                  }}
                  label="Template Name"
                  fullWidth
                  autoFocus
                  autoComplete="off"
                />
              )}
            />
            <Controller
              name={'templateDescription'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'templateDescription',
                  }}
                  InputLabelProps={{
                    htmlFor: 'templateDescription',
                  }}
                  label="Template Description"
                  fullWidth
                  autoComplete="off"
                />
              )}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const updatedTemplate: GroupTemplate = {
                ...selectedTemplate,
                groupTemplateName: getValues('templateName'),
                groupTemplateDesc: getValues('templateDescription'),
              };
              mutate(updatedTemplate);
            }}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending || !isDirty,
      }}
    />
  );
}
